import { Button, Card, Form, Input, Select, Table, TableColumnsType } from 'antd'
import { HistoryType, StakingTokenType } from 'Modules'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ButtonExport from 'component/button/button-export'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { HISTORY_TYPE, HISTORY_TYPE_ARRAY } from 'constants/history-type'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { convertDateToString } from 'utils/format-time'
import { logError } from 'utils/logger'

import Overview from './overview'

import './style.scss'

export default function PoolDetail() {
  const [form] = Form.useForm()
  const location = useParams()

  const poolId = location.id

  const [dataStaking, setDataStaking] = useState<StakingTokenType | null>(null)

  const [transactions, setListTransaction] = useState<HistoryType[]>([])

  const [filter, setFilter] = useState<any>({})

  const [pagination, setPagination] = useState({
    page: DEFAULT_PAGE_INDEX,
    limit: DEFAULT_PAGE_SIZE,
  })

  const [total, setTotal] = useState<number>(0)

  const [address, setAddress] = useState('')

  const [type, setType] = useState<number | null>(null)

  useEffect(() => {
    getDetailStakingToken(poolId)
  }, [poolId])

  useEffect(() => {
    if (dataStaking) {
      getDataTransactions({
        ...pagination,
        ...filter,
        contractAddress: dataStaking?.contractAddress,
        ownerAddress: address,
        type: type,
      })
    }
  }, [pagination, filter, dataStaking, address, type])

  const getDetailStakingToken = async (poolId: any) => {
    try {
      const res = await AppAdmin.getDetailStakingToken(poolId)
      if (res?.data.success === true) {
        setDataStaking(res?.data.data)
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const getDataTransactions = async (payload: any) => {
    try {
      const res = await AppAdmin.getListTransactionStaking(payload)
      if (res?.data.success === true) {
        const { results, totalResults } = res.data.data || {}
        setListTransaction(results || [])
        setTotal(totalResults)
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const columns: TableColumnsType<HistoryType> = [
    {
      title: 'Address',
      dataIndex: 'ownerAddress',
      key: 'ownerAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <LinkAddress address={text} />
        </div>
      ),
    },

    {
      title: 'Type',
      dataIndex: 'byType',
      key: 'byType',
      render: (byType) => {
        const objectType = HISTORY_TYPE_ARRAY.find((c) => c.value === Number(byType))
        return <span>{objectType?.label || ''}</span>
      },
    },
    {
      title: 'Asset Token',
      dataIndex: 'assetToken',
      key: 'assetToken',
    },
    {
      title: 'Amount (MEY)',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Status',
      dataIndex: 'byStatus',
      key: 'byStatus',
      render: (status) =>
        status === 1 ? (
          <span className="status-pending">Pending</span>
        ) : status === 2 ? (
          <span className="status-success">Success</span>
        ) : (
          <span className="status-fail">Fail</span>
        ),
    },

    {
      title: 'Tx Hash',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <LinkAddress address={text} type="tx" />
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => convertDateToString(text),
    },
  ]

  const statusOptions = [
    {
      value: null,
      label: 'All',
    },
    ...HISTORY_TYPE_ARRAY.filter((c) =>
      [
        HISTORY_TYPE.STAKE_TOKEN,
        HISTORY_TYPE.UN_STAKE_TOKEN,
        HISTORY_TYPE.STAKE_TOKEN_REWARD,
        HISTORY_TYPE.STAKE_TOKEN_CLAIM_REWARD,
        HISTORY_TYPE.STAKE_TOKEN_WITHDRAW,
      ].includes(c.value)
    ),
  ]

  const additionalItems = [BREADCRUMBS.STAKING_TOKEN_DETAIL]

  return (
    <div className="main-section pool-detail">
      <Breadcrumb additionalItems={additionalItems} />
      <Overview />

      <div className="listUsers">
        <div className="main-title">{dataStaking?.name}</div>
        <Card style={{ margin: '0 auto' }}>
          <div className="formSearch">
            <Form
              form={form}
              layout="inline"
              onFinish={(values: any) => {
                setAddress(values.address)
                setFilter((prev: any) => ({
                  ...prev,
                  address: values.address,
                }))
              }}
              style={{ padding: '0px 0 10px ', justifyContent: 'start' }}
            >
              <Form.Item name="ownerAddress">
                <Input
                  allowClear
                  placeholder="Enter Address"
                  style={{ width: '100%' }}
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value)
                  }}
                />
              </Form.Item>
              <Form.Item name="type">
                <Select
                  showSearch
                  placeholder="Choose Type"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    String(optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare(String(optionB?.label ?? '').toLowerCase())
                  }
                  options={statusOptions}
                  value={type}
                  onChange={(value) => setType(value)}
                  style={{ width: '200px', textAlign: 'start' }}
                />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
              </Form.Item>
              <Form.Item>
                <ButtonExport
                  fileType="staking_token_transactions"
                  disabled={!dataStaking?.contractAddress}
                  onExport={() =>
                    AppAdmin.exportTransactionStaking({
                      contractAddress: dataStaking?.contractAddress as string,
                      ownerAddress: address,
                      type,
                    })
                  }
                />
              </Form.Item>
            </Form>
          </div>
          <Table<HistoryType>
            columns={columns}
            dataSource={transactions}
            scroll={{ x: 1000 }}
            bordered
            pagination={
              total <= 10
                ? false
                : {
                    current: pagination.page,
                    pageSize: pagination.limit,
                    pageSizeOptions: DEFAULT_PAGE_OPTIONS,
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    onShowSizeChange(current, pageSize) {
                      setPagination({
                        page: current,
                        limit: pageSize,
                      })
                    },
                    onChange(page, pageSize) {
                      setPagination({
                        page: page,
                        limit: pageSize,
                      })
                    },
                  }
            }
          />
        </Card>
      </div>
    </div>
  )
}
